import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { apiBaseUrl } from '../../../util/api';
import { FieldSelect, FieldTextInput, PrimaryButton } from '../../../components';
import { required, emailFormatValid, composeValidators } from '../../../util/validators';

import css from './IdealSessionForm.module.css';

const IdealSessionForm = props => {
  const { className, rootClassName } = props;
  const classes = classNames(rootClassName || css.root, className);
  const intl = useIntl();
  const [submitStatus, setSubmitStatus] = useState(null);

  const categoryOptions = [
    { key: 'WeigthLoss', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.WeigthLoss' }) },
    { key: 'MuscleGain', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.MuscleGain' }) },
    { key: 'Cardio', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Cardio' }) },
    { key: 'Flexibility', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Flexibility' }) },
    { key: 'Strength', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Strength' }) },
    { key: 'Endurance', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Endurance' }) },
    { key: 'Nutrition', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Nutrition' }) },
    { key: 'Tonification', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Tonification' }) },
    { key: 'FunctionalStrength', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.FunctionalStrength' }) },
    { key: 'Posture', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Posture' }) },
    { key: 'CardioSculpt', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.CardioSculpt' }) },
    { key: 'Hypertrophy', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Hypertrophy' }) },
    { key: 'CardiRepiratory', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.CardiRepiratory' }) },
    { key: 'Relaxation', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Relaxation' }) },
    { key: 'BonesReinforcement', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.BonesReinforcement' }) },
    { key: 'LocalWeigthLoss', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.LocalWeigthLoss' }) },
    { key: 'Explosivity', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Explosivity' }) },
    { key: 'BalanceAndStability', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.BalanceAndStability' }) },
    { key: 'Pregnancy', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Pregnancy' }) },
    { key: 'Other', label: intl.formatMessage({ id: 'CustomExtendedDataField.CateoryFields.option.Other' }) }
  ];

  const levelOptions = [
    { key: 'Beginners', label: intl.formatMessage({ id: 'CustomExtendedDataField.Level.option.Beginners' }) },
    { key: 'Intermediate', label: intl.formatMessage({ id: 'CustomExtendedDataField.Level.option.Intermediate' }) },
    { key: 'Advanced', label: intl.formatMessage({ id: 'CustomExtendedDataField.Level.option.Advanced' }) },
  ];

  const onSubmit = async values => {
    try {
      const response = await fetch(`${apiBaseUrl()}/api/submit-ideal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (e) {
      setSubmitStatus('error');
    }
  };

  const categoryLabel = intl.formatMessage({ id: 'IdealSessionForm.categoryLabel' });
  const levelLabel = intl.formatMessage({ id: 'IdealSessionForm.levelLabel' });
  const locationLabel = intl.formatMessage({ id: 'IdealSessionForm.locationLabel' });
  const emailLabel = intl.formatMessage({ id: 'IdealSessionForm.emailLabel' });

  const requiredMsg = intl.formatMessage({ id: 'IdealSessionForm.required', defaultMessage: 'Required' });
  const invalidEmailMsg = intl.formatMessage({ id: 'IdealSessionForm.invalidEmail', defaultMessage: 'Invalid email' });

  return (
    <div className={classes}>
      <h2 className={css.title}>
        <FormattedMessage id="IdealSessionForm.title" />
      </h2>
      <FinalForm
        onSubmit={onSubmit}
        render={fieldRenderProps => {
          const { handleSubmit, invalid, pristine, submitting } = fieldRenderProps;
          const submitDisabled = invalid || pristine || submitting || submitStatus === 'success';

          return (
            <form onSubmit={handleSubmit} className={css.form}>
              <div className={css.row}>
                <FieldSelect id="category" name="category" label={categoryLabel} validate={required(requiredMsg)}>
                  <option disabled value="">
                    {intl.formatMessage({ id: 'IdealSessionForm.categoryPlaceholder' })}
                  </option>
                  {categoryOptions.map(opt => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </FieldSelect>

                <FieldSelect id="level" name="level" label={levelLabel} validate={required(requiredMsg)}>
                  <option disabled value="">
                    {intl.formatMessage({ id: 'IdealSessionForm.levelPlaceholder' })}
                  </option>
                  {levelOptions.map(opt => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </FieldSelect>
              </div>
              <div className={css.row}>
                <FieldTextInput
                  id="location"
                  name="location"
                  type="text"
                  label={locationLabel}
                  placeholder={intl.formatMessage({ id: 'IdealSessionForm.locationPlaceholder' })}
                  validate={required(requiredMsg)}
                />
                <FieldTextInput
                  id="email"
                  name="email"
                  type="email"
                  label={emailLabel}
                  placeholder={intl.formatMessage({ id: 'IdealSessionForm.emailPlaceholder' })}
                  validate={composeValidators(required(requiredMsg), emailFormatValid(invalidEmailMsg))}
                />
              </div>

              <FieldTextInput
                className={css.optionalField}
                id="notes"
                name="notes"
                type="textarea"
                label={intl.formatMessage({ id: 'IdealSessionForm.notesLabel' })}
                placeholder={intl.formatMessage({ id: 'IdealSessionForm.notesPlaceholder' })}
              />

              <PrimaryButton
                type="submit"
                inProgress={submitting}
                disabled={submitDisabled}
                className={css.submitButton}
              >
                <FormattedMessage id="IdealSessionForm.submit" />
              </PrimaryButton>

              {submitStatus === 'success' && (
                <p className={css.successMessage}>
                  <FormattedMessage id="IdealSessionForm.success" />
                </p>
              )}
              {submitStatus === 'error' && (
                <p className={css.errorMessage}>
                  <FormattedMessage id="IdealSessionForm.error" />
                </p>
              )}
            </form>
          );
        }}
      />
    </div>
  );
};

export default IdealSessionForm;
